import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// -- Utility: derive full spec from width/ratio/rim (metric mm + inches) -----
function specs(width, ratio, rim) {
  const sidewallMm = width * (ratio / 100);
  const sidewallIn = sidewallMm / 25.4;
  const diameterMm = rim * 25.4 + 2 * sidewallMm;
  const diameterIn = diameterMm / 25.4;
  const widthIn = width / 25.4;
  const circIn = Math.PI * diameterIn;
  const circMm = Math.PI * diameterMm;
  const revPerMile = 63360 / circIn;
  const revPerKm = 1e6 / circMm;
  return { width, ratio, rim, sidewallMm, sidewallIn, diameterMm, diameterIn, widthMm: width, widthIn, circIn, circMm, revPerMile, revPerKm };
}

const G = (n, d = 1) => Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

/* -------- SVG diagrams matching the reference -------- */
// Side view (tread strip) + Front view (round wheel) with blue dimension labels.
function TireDiagram({ s, size = 380, showBoth = true }) {
  const scale = size / 380;
  const H = 260 * scale;
  const W = 440 * scale;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="max-w-full">
      {/* Front view (round tire) — RIGHT side */}
      {showBoth && (
        <g transform={`translate(${140 * scale}, 0)`}>
          <circle cx={110 * scale} cy={110 * scale} r={95 * scale} fill="#0f172a"/>
          <circle cx={110 * scale} cy={110 * scale} r={55 * scale} fill="#e5e7eb"/>
          <line x1={110 * scale - 55 * scale} y1={110 * scale} x2={110 * scale + 55 * scale} y2={110 * scale} stroke="#0f172a" strokeWidth="4"/>
          <line x1={110 * scale} y1={110 * scale - 55 * scale} x2={110 * scale} y2={110 * scale + 55 * scale} stroke="#0f172a" strokeWidth="4"/>
          {/* Sidewall dimension (top-right) */}
          <line x1={(110 + 95) * scale + 10} y1={(110 - 95) * scale} x2={(110 + 95) * scale + 10} y2={(110 - 55) * scale} stroke="#2563eb" strokeWidth="1"/>
          <text x={(110 + 95) * scale + 15} y={(110 - 80) * scale} fill="#2563eb" fontSize={11 * scale}>{G(s.sidewallIn, 1)} in</text>
          <text x={(110 + 95) * scale + 15} y={(110 - 65) * scale} fill="#2563eb" fontSize={11 * scale}>{G(s.sidewallMm, 0)} mm</text>
          {/* Rim dimension (middle-right) */}
          <line x1={(110 + 95) * scale + 10} y1={(110 - 55) * scale} x2={(110 + 95) * scale + 10} y2={(110 + 55) * scale} stroke="#2563eb" strokeWidth="1"/>
          <text x={(110 + 95) * scale + 15} y={(110 + 0) * scale} fill="#2563eb" fontSize={11 * scale}>{s.rim} in</text>
          <text x={(110 + 95) * scale + 15} y={(110 + 15) * scale} fill="#2563eb" fontSize={11 * scale}>{G(s.rim * 25.4, 0)} mm</text>
        </g>
      )}
      {/* Side view (tread strip) — LEFT */}
      <g>
        <rect x={30 * scale} y={15 * scale} width={70 * scale} height={190 * scale} fill="#1e293b" rx={6}/>
        <line x1={35 * scale} y1={30 * scale} x2={35 * scale} y2={190 * scale} stroke="#475569" strokeWidth="1"/>
        <line x1={65 * scale} y1={30 * scale} x2={65 * scale} y2={190 * scale} stroke="#475569" strokeWidth="1"/>
        <line x1={95 * scale} y1={30 * scale} x2={95 * scale} y2={190 * scale} stroke="#475569" strokeWidth="1"/>
        {/* Diameter dim (vertical, left of tread) */}
        <line x1={20 * scale} y1={15 * scale} x2={20 * scale} y2={205 * scale} stroke="#2563eb" strokeWidth="1"/>
        <line x1={16 * scale} y1={15 * scale} x2={24 * scale} y2={15 * scale} stroke="#2563eb"/>
        <line x1={16 * scale} y1={205 * scale} x2={24 * scale} y2={205 * scale} stroke="#2563eb"/>
        <text x={0} y={110 * scale} fill="#2563eb" fontSize={11 * scale}>{G(s.diameterIn, 1)} in</text>
        <text x={0} y={125 * scale} fill="#2563eb" fontSize={11 * scale}>{G(s.diameterMm, 0)} mm</text>
        {/* Width dim (horizontal, below tread) */}
        <line x1={30 * scale} y1={220 * scale} x2={100 * scale} y2={220 * scale} stroke="#2563eb" strokeWidth="1"/>
        <text x={30 * scale} y={235 * scale} fill="#2563eb" fontSize={11 * scale}>{G(s.widthIn, 1)} in</text>
        <text x={30 * scale} y={250 * scale} fill="#2563eb" fontSize={11 * scale}>{s.widthMm} mm</text>
      </g>
    </svg>
  );
}

/* -------- Main tabs -------- */
export default function TireSizeCalc() {
  const [tab, setTab] = useState('single');
  return (
    <CalcShell look="cnet" showActions={false}
      title="Tire Size Calculator"
      subtitle="Find the dimensions of a tire from its code, browse alternative sizes for any wheel, and compare two tires side-by-side."
      about={<><p>Enter the numeric tire code (Width / Aspect Ratio / Rim) to see the exact diameter, sidewall height, circumference, and revolutions per mile. Use the compare tab to see how a swap will change your speedometer reading.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setTab('single')} data-testid="tire-tab-single"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='single'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Tire Size Calculator</button>
        <button onClick={()=>setTab('compare')} data-testid="tire-tab-compare"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='compare'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Tire Size Comparison</button>
      </div>
      {tab === 'single' ? <SingleTire/> : <CompareTires/>}
    </CalcShell>
  );
}

/* -------- Single-tire view with optional wheel-swap alt-sizes table -------- */
function SingleTire() {
  const [width, setWidth] = useState(265);
  const [ratio, setRatio] = useState(70);
  const [rim, setRim] = useState(17);
  const [swap, setSwap] = useState('no');            // Change to a new wheel size?
  const [newRim, setNewRim] = useState(19);
  const s = useMemo(() => specs(width, ratio, rim), [width, ratio, rim]);
  const wheelForAlts = swap === 'yes' ? newRim : rim;

  // Build alternative-size list for the (new) wheel size, ±3% diameter
  const alts = useMemo(() => {
    const targetDia = s.diameterIn;
    const rows = [];
    for (let w = 195; w <= 335; w += 5) {
      for (let r = 30; r <= 85; r += 5) {
        const alt = specs(w, r, wheelForAlts);
        const diaPct = (alt.diameterIn - targetDia) / targetDia * 100;
        if (Math.abs(diaPct) <= 3) {
          const widthPct = (alt.widthMm - s.widthMm) / s.widthMm * 100;
          const sidePct  = (alt.sidewallMm - s.sidewallMm) / s.sidewallMm * 100;
          rows.push({ size: `${w}/${r}R${wheelForAlts}`, dia: alt.diameterIn, diaPct, wMm: alt.widthMm, widthPct, sideIn: alt.sidewallIn, sidePct, match: Math.abs(diaPct) < 0.5 && w === width });
        }
      }
    }
    return rows.sort((a,b) => a.wMm !== b.wMm ? a.wMm - b.wMm : a.dia - b.dia);
  }, [s, wheelForAlts, width]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Result table + inputs (left) */}
        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body p-0">
            <table className="w-full text-sm border-collapse">
              <tbody>
                <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Tire Height (Diameter)</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.diameterIn, 1)} in</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.diameterMm, 0)} mm</td></tr>
                <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Wheel Size</td><td className="px-3 py-2 text-right whitespace-nowrap">{s.rim} in</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.rim * 25.4, 0)} mm</td></tr>
                <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Sidewall Height</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.sidewallIn, 1)} in</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.sidewallMm, 0)} mm</td></tr>
                <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Tire Width</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.widthIn, 1)} in</td><td className="px-3 py-2 text-right whitespace-nowrap">{s.widthMm} mm</td></tr>
                <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Tire Circumference</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.circIn, 1)} in</td><td className="px-3 py-2 text-right whitespace-nowrap">{G(s.circMm, 0)} mm</td></tr>
                <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Revolutions per Mile</td><td className="px-3 py-2 text-right whitespace-nowrap" colSpan={2}>{G(s.revPerMile, 1)}</td></tr>
                <tr><td className="px-3 py-2 bg-slate-50 font-medium">Revolutions per Kilometer</td><td className="px-3 py-2 text-right whitespace-nowrap" colSpan={2}>{G(s.revPerKm, 1)}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="font-semibold text-slate-900 mb-2">Tire Size:</div>
            <div className="flex items-center gap-2">
              <input type="number" value={width} onChange={e=>setWidth(+e.target.value||0)} className="w-20 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire-width"/>
              <span className="font-bold">/</span>
              <input type="number" value={ratio} onChange={e=>setRatio(+e.target.value||0)} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire-ratio"/>
              <span className="font-bold">R</span>
              <input type="number" value={rim} onChange={e=>setRim(+e.target.value||0)} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire-rim"/>
            </div>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="font-medium">Change to a new wheel size?</span>
              <label className="flex items-center gap-1"><input type="radio" name="swap" checked={swap==='yes'} onChange={()=>setSwap('yes')} data-testid="tire-swap-yes"/> Yes</label>
              <label className="flex items-center gap-1"><input type="radio" name="swap" checked={swap==='no'} onChange={()=>setSwap('no')} data-testid="tire-swap-no"/> No</label>
              {swap === 'yes' && (
                <div className="flex items-center gap-2">
                  <span>New wheel:</span>
                  <input type="number" value={newRim} onChange={e=>setNewRim(+e.target.value||0)} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire-new-rim"/>
                  <span>inches</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Diagram (right) */}
        <div className="flex items-start justify-center pt-4">
          <TireDiagram s={s} size={380}/>
        </div>
      </div>

      {/* Alternative sizes table — always shown */}
      <div className="mt-8">
        <h3 className="text-lg font-bold text-slate-900 mb-3">
          The following are alternative tire sizes for a {wheelForAlts}-inch wheel.
        </h3>
        <div className="max-h-[520px] overflow-auto border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr>
              <th className="px-3 py-2 text-left">Tire Size</th>
              <th className="px-3 py-2 text-right">Tire Height / Diameter (inch)</th>
              <th className="px-3 py-2 text-right">Tire Width (mm)</th>
              <th className="px-3 py-2 text-right">Sidewall Height (inch)</th>
            </tr></thead>
            <tbody>
              {alts.map((r, i) => (
                <tr key={r.size} className={`border-t border-slate-100 ${r.match ? 'bg-green-100 font-semibold' : (i % 2 ? 'bg-slate-50' : '')}`}>
                  <td className="px-3 py-1.5 text-blue-700 underline">{r.size}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">{G(r.dia, 1)} ({G(r.diaPct, 2)}%)</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">{r.wMm} ({G(r.widthPct, 2)}%)</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">{G(r.sideIn, 1)} ({G(r.sidePct, 2)}%)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* -------- Compare two tires -------- */
function CompareTires() {
  const [t1, setT1] = useState({ w: 265, r: 70, rim: 17 });
  const [t2, setT2] = useState({ w: 265, r: 40, rim: 23 });
  const s1 = useMemo(() => specs(t1.w, t1.r, t1.rim), [t1]);
  const s2 = useMemo(() => specs(t2.w, t2.r, t2.rim), [t2]);
  const diff = (a, b) => a === 0 ? 0 : (b - a) / a * 100;
  const speedTable = [20, 40, 60, 80, 100, 120];

  return (
    <>
      <div>
        <div className="cnet-result-hdr">Result</div>
        <div className="cnet-result-body p-0">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-blue-800 text-white"><tr>
              <th className="px-3 py-2 text-left"></th>
              <th className="px-3 py-2 text-center" colSpan={2}>Tire 1<br/><span className="font-normal text-xs">{t1.w}/{t1.r}R{t1.rim}</span></th>
              <th className="px-3 py-2 text-center" colSpan={2}>Tire 2<br/><span className="font-normal text-xs">{t2.w}/{t2.r}R{t2.rim}</span></th>
              <th className="px-3 py-2 text-right">Difference</th>
            </tr></thead>
            <tbody>
              {[
                ['Tire Height (Diameter)', s1.diameterIn, 'in', s1.diameterMm, 'mm', s2.diameterIn, 'in', s2.diameterMm, 'mm'],
                ['Wheel Size',              s1.rim,          'in', s1.rim*25.4, 'mm', s2.rim,          'in', s2.rim*25.4, 'mm'],
                ['Sidewall Height',         s1.sidewallIn,   'in', s1.sidewallMm,'mm', s2.sidewallIn,   'in', s2.sidewallMm,'mm'],
                ['Tire Width',              s1.widthIn,      'in', s1.widthMm,  'mm', s2.widthIn,      'in', s2.widthMm,  'mm'],
                ['Tire Circumference',      s1.circIn,       'in', s1.circMm,   'mm', s2.circIn,       'in', s2.circMm,   'mm'],
              ].map((row, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="px-3 py-2 bg-slate-50 font-medium whitespace-nowrap">{row[0]}</td>
                  <td className="px-3 py-2 text-right bg-blue-50">{G(row[1], 1)} {row[2]}</td>
                  <td className="px-3 py-2 text-right bg-blue-50">{G(row[3], 0)} {row[4]}</td>
                  <td className="px-3 py-2 text-right bg-green-50">{G(row[5], 1)} {row[6]}</td>
                  <td className="px-3 py-2 text-right bg-green-50">{G(row[7], 0)} {row[8]}</td>
                  <td className="px-3 py-2 text-right font-semibold">{G(Math.abs(diff(row[1], row[5])), 2)}%</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Revolutions per Mile</td>
                <td className="px-3 py-2 text-right bg-blue-50" colSpan={2}>{G(s1.revPerMile, 1)}</td>
                <td className="px-3 py-2 text-right bg-green-50" colSpan={2}>{G(s2.revPerMile, 1)}</td>
                <td className="px-3 py-2 text-right font-semibold">{G(Math.abs(diff(s1.revPerMile, s2.revPerMile)), 2)}%</td>
              </tr>
              <tr className="border-t border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium">Revolutions per Kilometer</td>
                <td className="px-3 py-2 text-right bg-blue-50" colSpan={2}>{G(s1.revPerKm, 1)}</td>
                <td className="px-3 py-2 text-right bg-green-50" colSpan={2}>{G(s2.revPerKm, 1)}</td>
                <td className="px-3 py-2 text-right font-semibold">{G(Math.abs(diff(s1.revPerKm, s2.revPerKm)), 2)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Speedometer Difference:</h3>
        <div className="overflow-auto border border-slate-200 rounded">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-slate-200"><td className="px-3 py-2 bg-slate-50 font-medium whitespace-nowrap">Tire 1 (mph or km/h):</td>{speedTable.map(v => <td key={v} className="px-3 py-2 text-center">{v}</td>)}</tr>
              <tr><td className="px-3 py-2 bg-slate-50 font-medium whitespace-nowrap">Tire 2 (mph or km/h):</td>{speedTable.map(v => <td key={v} className="px-3 py-2 text-center">{G(v * (s2.diameterIn / s1.diameterIn), 1)}</td>)}</tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="text-center">
          <TireDiagram s={s1} size={340}/>
          <div className="text-blue-700 font-semibold mt-2">Tire 1</div>
        </div>
        <div className="text-center">
          <TireDiagram s={s2} size={340}/>
          <div className="text-emerald-700 font-semibold mt-2">Tire 2</div>
        </div>
      </div>

      {/* Inputs */}
      <div className="mt-8 p-4 border border-slate-200 rounded-lg bg-slate-50 max-w-xl">
        <div className="grid grid-cols-[auto_1fr] items-center gap-y-3 gap-x-4">
          <div className="font-semibold">Tire 1:</div>
          <div className="flex items-center gap-2">
            <input type="number" value={t1.w}   onChange={e=>setT1({...t1, w:   +e.target.value||0})} className="w-20 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire1-w"/>
            <span className="font-bold">/</span>
            <input type="number" value={t1.r}   onChange={e=>setT1({...t1, r:   +e.target.value||0})} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire1-r"/>
            <span className="font-bold">R</span>
            <input type="number" value={t1.rim} onChange={e=>setT1({...t1, rim: +e.target.value||0})} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire1-rim"/>
          </div>
          <div className="font-semibold">Tire 2:</div>
          <div className="flex items-center gap-2">
            <input type="number" value={t2.w}   onChange={e=>setT2({...t2, w:   +e.target.value||0})} className="w-20 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire2-w"/>
            <span className="font-bold">/</span>
            <input type="number" value={t2.r}   onChange={e=>setT2({...t2, r:   +e.target.value||0})} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire2-r"/>
            <span className="font-bold">R</span>
            <input type="number" value={t2.rim} onChange={e=>setT2({...t2, rim: +e.target.value||0})} className="w-16 border border-slate-400 rounded px-2 py-1 text-center" data-testid="tire2-rim"/>
          </div>
        </div>
      </div>
    </>
  );
}
